import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Check, Crown, Zap, BarChart3, MessageSquare, Users, Shield, Star } from "lucide-react";
import { toast } from "sonner";

const packages = {
  scout_pro: {
    name: "Scout Pro", price: "$9.99", period: "/month",
    features: ["Unlimited Spotlight access", "Advanced scout filters", "Priority messaging", "Profile badge"],
    icon: Zap, color: "#074572"
  },
  team_pro: {
    name: "Team Pro", price: "$19.99", period: "/month",
    features: ["Everything in Scout Pro", "Team management tools", "Full analytics dashboard", "Bulk messaging", "API access"],
    icon: Crown, color: "#F85037"
  },
};

export default function PremiumPage() {
  const { user, checkAuth } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) {
      toast.error("Payment status check timed out. Please refresh.");
      setCheckingPayment(false);
      return;
    }
    setCheckingPayment(true);
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "paid") {
        toast.success("Payment successful! Welcome to Premium!");
        await checkAuth();
        setCheckingPayment(false);
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch {
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    }
  };

  const handleSubscribe = async (packageId) => {
    setLoading(packageId);
    try {
      const originUrl = window.location.origin;
      const { data } = await api.post("/payments/checkout", {
        package_id: packageId,
        origin_url: originUrl,
      });
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start checkout");
    } finally {
      setLoading(null);
    }
  };

  const isPremium = user?.is_premium;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8" data-testid="premium-page">
      {/* Header */}
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#F85037]/5 rounded-full mb-4">
          <Crown size={14} className="text-[#F85037]" />
          <span className="text-xs font-semibold tracking-wide uppercase text-[#F85037]">Go Premium</span>
        </div>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-medium text-zinc-900 mb-3" style={{ fontFamily: 'Outfit' }}>
          Unlock Your Full Potential
        </h1>
        <p className="text-base text-zinc-500 max-w-lg mx-auto">
          Get advanced tools to discover talent, grow your network, and stand out in the football community.
        </p>
      </div>

      {checkingPayment && (
        <div className="mb-8 p-4 bg-[#074572]/5 border border-[#074572]/20 rounded-lg text-center">
          <div className="w-6 h-6 border-2 border-[#074572] border-t-transparent rounded-full animate-spin mx-auto mb-2" />
          <p className="text-sm text-[#074572] font-medium">Verifying your payment...</p>
        </div>
      )}

      {isPremium && (
        <div className="mb-8 p-6 bg-gradient-to-r from-[#074572] to-[#0a5a8f] rounded-xl text-white text-center" data-testid="premium-active-badge">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Crown size={20} className="text-amber-300" />
            <h2 className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>Premium Active</h2>
          </div>
          <p className="text-white/70 text-sm">You're on the <strong className="capitalize">{user?.premium_plan?.replace("_", " ")}</strong> plan</p>
        </div>
      )}

      {/* Pricing Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {Object.entries(packages).map(([id, pkg]) => {
          const Icon = pkg.icon;
          const isCurrentPlan = isPremium && user?.premium_plan === id;
          return (
            <div key={id} className={`bg-white border rounded-xl p-8 relative ${id === "team_pro" ? "border-[#F85037] shadow-lg" : "border-[#E6E6E6]"}`} data-testid={`premium-card-${id}`}>
              {id === "team_pro" && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-[#F85037] text-white text-[10px] font-bold rounded-full uppercase tracking-wider">
                  Most Popular
                </div>
              )}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${pkg.color}10` }}>
                  <Icon size={20} style={{ color: pkg.color }} />
                </div>
                <div>
                  <h3 className="font-semibold text-zinc-900" style={{ fontFamily: 'Outfit' }}>{pkg.name}</h3>
                </div>
              </div>
              <div className="flex items-baseline gap-1 mb-6">
                <span className="text-4xl font-bold text-zinc-900" style={{ fontFamily: 'Outfit' }}>{pkg.price}</span>
                <span className="text-sm text-zinc-400">{pkg.period}</span>
              </div>
              <Separator className="bg-[#E6E6E6] mb-6" />
              <ul className="space-y-3 mb-8">
                {pkg.features.map(f => (
                  <li key={f} className="flex items-center gap-2 text-sm text-zinc-600">
                    <Check size={14} className="text-green-500 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              {isCurrentPlan ? (
                <Button disabled className="w-full bg-green-50 text-green-700 border border-green-200">
                  <Check size={16} className="mr-2" /> Current Plan
                </Button>
              ) : (
                <Button
                  className={`w-full ${id === "team_pro" ? "bg-[#F85037] hover:bg-[#e04530]" : "bg-[#074572] hover:bg-[#053a60]"} text-white font-medium`}
                  onClick={() => handleSubscribe(id)}
                  disabled={loading === id}
                  data-testid={`subscribe-${id}`}
                >
                  {loading === id ? "Processing..." : "Subscribe Now"}
                </Button>
              )}
            </div>
          );
        })}
      </div>

      {/* Features */}
      <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { icon: Shield, label: "Verified Badge", desc: "Stand out with a premium badge" },
          { icon: BarChart3, label: "Analytics", desc: "Deep talent insights" },
          { icon: MessageSquare, label: "Priority DMs", desc: "Get seen first" },
          { icon: Star, label: "Spotlight", desc: "Featured placement" },
        ].map(f => (
          <div key={f.label} className="bg-white border border-[#E6E6E6] rounded-lg p-4 text-center">
            <f.icon size={20} className="mx-auto text-[#074572] mb-2" />
            <p className="text-sm font-medium text-zinc-900">{f.label}</p>
            <p className="text-xs text-zinc-400 mt-0.5">{f.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
